"use client";

import { useState, useEffect } from "react";
import {
  RecurringOrder,
  RECURRING_STATUS,
  RECURRING_STATUS_NAME,
  formatFrequency,
} from "../services/recurring-order-service";
import { ServiceMapService } from "../../order/services/service-map-service";
import { ServiceAddonService } from "../../order/services/service-addon-service";
import { isSuccess } from "../../../models/response/base-response";

interface RecurringOrderDetailModalProps {
  open: boolean;
  onClose: () => void;
  recurring: RecurringOrder | null;
  onToggleStatus?: (id: number) => void;
  onEnd?: (id: number) => void;
  onEdit?: (recurring: RecurringOrder) => void;
  customerName?: string;
  branchName?: string;
}

type Opt = { id: number; name: string; price: number; unitName: string };

const statusColor: Record<number, string> = {
  [RECURRING_STATUS.ACTIVE]: "bg-green-100 text-green-700 ring-1 ring-green-200",
  [RECURRING_STATUS.PAUSED]: "bg-yellow-100 text-yellow-700 ring-1 ring-yellow-200",
  [RECURRING_STATUS.ENDED]: "bg-gray-100 text-gray-500 ring-1 ring-gray-200",
};

const formatDate = (dateStr?: string | null): string => {
  if (!dateStr) return "—";
  if (dateStr.includes("/")) return dateStr;
  const datePart = dateStr.split("T")[0];
  const parts = datePart.split("-");
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return dateStr;
};

const formatDateTime = (raw?: string | null): string => {
  if (!raw) return "—";
  const d = new Date(raw);
  if (isNaN(d.getTime())) return raw;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const money = (n?: number | null) =>
  n ? `${Number(n).toLocaleString("vi-VN")} đ` : "0 đ";

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex justify-between gap-4 py-1.5 text-sm">
    <span className="text-gray-500 shrink-0">{label}</span>
    <span className="font-medium text-gray-800 text-right">{children}</span>
  </div>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="rounded-xl border border-gray-100 bg-gray-50/60 p-4">
    <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-gray-400">{title}</h3>
    {children}
  </div>
);

const RecurringOrderDetailModal: React.FC<RecurringOrderDetailModalProps> = ({
  open,
  onClose,
  recurring,
  customerName,
  branchName,
}) => {
  // Nạp tên/đơn vị/giá service+addon theo chi nhánh của đơn để tra (item chỉ có id)
  const [svcOptions, setSvcOptions] = useState<Opt[]>([]);
  const [addonOptions, setAddonOptions] = useState<Opt[]>([]);
  const { fetch: fetchSvc } = ServiceMapService.listActive();
  const { fetch: fetchAddon } = ServiceAddonService.listActive();
  const branchId = recurring?.branch_id;

  useEffect(() => {
    if (!open || !branchId) {
      setSvcOptions([]);
      setAddonOptions([]);
      return;
    }
    let active = true;
    (async () => {
      try {
        const res = await fetchSvc({ status: 1, branch_id: branchId, limit: 1000 } as any);
        if (active && res && isSuccess(res)) {
          const list = (res as any)?.data?.list ?? [];
          setSvcOptions(
            list
              .filter((s: any) => s?.service?.id)
              .map((s: any) => ({
                id: s.id,
                name:
                  [s.service?.name, s.serviceType?.name || s.service_type?.name]
                    .filter(Boolean)
                    .join(" - ") || `DV #${s.id}`,
                price: Number(s.price) || 0,
                unitName: s.unit_name || "",
              })),
          );
        }
      } catch {
        if (active) setSvcOptions([]);
      }
    })();
    (async () => {
      try {
        const res = await fetchAddon({ status: 1, branch_id: branchId, limit: 1000 } as any);
        if (active && res && isSuccess(res)) {
          const list = (res as any)?.data?.list ?? [];
          setAddonOptions(
            list.map((a: any) => ({
              id: a.id,
              name: a.name || a.addon_name || `Addon #${a.id}`,
              price: Number(a.price) || 0,
              unitName: "",
            })),
          );
        }
      } catch {
        if (active) setAddonOptions([]);
      }
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, branchId]);

  if (!open || !recurring) return null;

  const r = recurring;
  const items = r.template_items ?? [];

  const lines = items.map((item) => {
    const found =
      item.item_type === 1
        ? svcOptions.find((s) => Number(s.id) === Number(item.item_id))
        : addonOptions.find((a) => Number(a.id) === Number(item.item_id));
    const price = Number(item.price ?? found?.price ?? 0);
    return {
      key: `${item.item_type}-${item.item_id}`,
      typeLabel: item.item_type === 1 ? "Dịch vụ" : "Phụ phí",
      name: found?.name || `#${item.item_id}`,
      unitName: found?.unitName || "",
      qty: Number(item.quantity) || 0,
      price,
      lineTotal: price * (Number(item.quantity) || 0),
    };
  });
  const estimate = lines.reduce((s, l) => s + l.lineTotal, 0);
  const hasPrice = lines.some((l) => l.price > 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 border-b px-6 py-4">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold text-gray-900">
                Đơn định kỳ của {customerName || `KH #${r.customer_id}`}
              </h2>
              <span
                className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${statusColor[r.status]}`}
              >
                {RECURRING_STATUS_NAME[r.status]}
              </span>
            </div>
            <p className="mt-0.5 text-xs text-gray-400">
              Mã #{r.id} · {branchName || `CN #${r.branch_id}`}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-xl leading-none text-gray-400 hover:text-gray-600"
          >
            &times;
          </button>
        </div>

        {/* Body */}
        <div className="grid gap-4 overflow-y-auto px-6 py-5 md:grid-cols-2">
          {/* Lịch chạy */}
          <Section title="Lịch chạy">
            <Row label="Tần suất">{formatFrequency(r.frequency_type, r.frequency_value)}</Row>
            <Row label="Giờ tạo đơn">{r.preferred_time}</Row>
            <Row label="Bắt đầu">{formatDate(r.start_date)}</Row>
            <Row label="Kết thúc">{r.end_date ? formatDate(r.end_date) : "Vô thời hạn"}</Row>
            <div className="my-1 border-t border-gray-200" />
            <Row label="Ngày tạo đơn tiếp">
              <span className="font-bold text-blue-600">{formatDate(r.next_order_date)}</span>
            </Row>
            <Row label="Số đơn đã tạo">
              <span className="font-bold text-blue-600">{r.total_orders_created}</span>
            </Row>
            {r.last_order_id ? (
              <Row label="Đơn gần nhất">
                #{r.last_order_id} · {formatDate(r.last_order_date)}
              </Row>
            ) : null}
          </Section>

          {/* Thiết lập đơn */}
          <Section title="Thông tin đơn">
            <Row label="Địa chỉ">{r.address || "—"}</Row>
            <Row label="Ghi chú">{r.note || "—"}</Row>
            <Row label="Thanh toán">{r.payment_method === 1 ? "Tiền mặt" : "QR Code"}</Row>
            <Row label="Công nợ">{r.is_debit ? "Có" : "Không"}</Row>
            <Row label="Chiết khấu">{r.amount_discount ? money(r.amount_discount) : "Không"}</Row>
            <Row label="Lấy hàng tận nơi">{r.is_pickup_laundry ? "Có" : "Không"}</Row>
            <Row label="Kiểm đếm khi nhận">
              {Number(r.requires_counting) === 1 ? (
                <span className="text-blue-600">Có</span>
              ) : (
                "Không"
              )}
            </Row>
            <Row label="Điền sẵn SL từ đơn gần nhất">
              {Number(r.prefill_count_from_last) === 1 ? "Có" : "Không"}
            </Row>
            <div className="my-1 border-t border-gray-200" />
            <Row label="Ngày tạo">{formatDateTime(r.created_at)}</Row>
            <Row label="Cập nhật gần nhất">{formatDateTime(r.updated_at)}</Row>
          </Section>

          {/* Dịch vụ template */}
          <div className="md:col-span-2">
            <Section title={`Sản phẩm / Dịch vụ (${lines.length})`}>
              {lines.length === 0 ? (
                <p className="text-sm text-gray-400">Chưa có dịch vụ</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs uppercase text-gray-400">
                        <th className="pb-2 pr-2 font-semibold">Tên</th>
                        <th className="pb-2 px-2 text-right font-semibold">Đơn giá</th>
                        <th className="pb-2 px-2 text-center font-semibold">SL</th>
                        <th className="pb-2 pl-2 text-right font-semibold">Thành tiền</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {lines.map((l) => (
                        <tr key={l.key}>
                          <td className="py-2 pr-2">
                            <div className="font-medium text-gray-800">{l.name}</div>
                            <div className="text-xs text-gray-400">
                              {l.typeLabel}
                              {l.unitName ? ` · ĐVT: ${l.unitName}` : ""}
                            </div>
                          </td>
                          <td className="py-2 px-2 text-right tabular-nums">{money(l.price)}</td>
                          <td className="py-2 px-2 text-center tabular-nums">{l.qty}</td>
                          <td className="py-2 pl-2 text-right font-medium tabular-nums">
                            {money(l.lineTotal)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t border-gray-200 font-semibold">
                        <td className="pt-2 pr-2" colSpan={3}>
                          Tạm tính mỗi kỳ
                        </td>
                        <td className="pt-2 pl-2 text-right tabular-nums">{money(estimate)}</td>
                      </tr>
                    </tfoot>
                  </table>
                  {!hasPrice && (
                    <p className="mt-2 text-xs text-amber-600">
                      Chưa lấy được đơn giá — số tạm tính có thể chưa đúng. Giá thật chốt khi tạo đơn.
                    </p>
                  )}
                </div>
              )}
            </Section>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end border-t px-6 py-4">
          <button
            onClick={onClose}
            className="rounded-lg border px-4 py-2 text-sm hover:bg-gray-50"
          >
            Đóng
          </button>
        </div>
      </div>
    </div>
  );
};

export default RecurringOrderDetailModal;
